import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import usePermission from '../../../hooks/usePermission';

const MasterRoleListing = () => {
  const { hasPermission } = usePermission();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active'
  });
  const [submitting, setSubmitting] = useState(false);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ show: false, role: null });

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` }
  });

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/superadmin/roles`, getAuthHeader());
      setRoles(res.data.roles || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch master roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setCurrentRoleId(null);
    setFormData({ name: '', description: '', status: 'active' });
    setShowModal(true);
  };

  const handleOpenEditModal = (role) => {
    setIsEditing(true);
    setCurrentRoleId(role.id);
    setFormData({
      name: role.name,
      description: role.description || '',
      status: role.status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (isEditing) {
        await axios.put(`${API_BASE}/superadmin/roles/${currentRoleId}`, formData, getAuthHeader());
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API_BASE}/superadmin/roles`, formData, getAuthHeader());
        toast.success('Role created successfully');
      }
      setShowModal(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.role) return;
    try {
      await axios.delete(`${API_BASE}/superadmin/roles/${deleteModal.role.id}`, getAuthHeader());
      toast.success('Role deleted successfully');
      setDeleteModal({ show: false, role: null });
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete role');
    }
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Page Title */}
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">Master Roles & Permissions</h4>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item"><Link to="/superadmin/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item active">Master Roles</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Header Actions */}
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="position-relative">
              <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-16" style={{ pointerEvents: 'none', zIndex: 5 }}></i>
              <input
                type="text"
                className="form-control ps-5"
                style={{ paddingLeft: '45px' }}
                placeholder="Search roles by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-6 text-md-end mt-3 mt-md-0">
            {hasPermission('master_roles.add') && (
              <button className="btn btn-primary" onClick={handleOpenAddModal}>
                <i className="ri-add-line align-bottom me-1"></i> Add Master Role
              </button>
            )}
          </div>
        </div>

        {/* Roles Table */}
        <div className="row">
          <div className="col-lg-12">
            <div className="card shadow-sm">
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 text-muted">Loading roles...</p>
                  </div>
                ) : filteredRoles.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="ri-shield-user-line display-4 text-secondary"></i>
                    <p className="mt-2">No master roles found.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Role Name</th>
                          <th>Description</th>
                          <th>Assigned Employees</th>
                          <th>Permissions Assigned</th>
                          <th>Status</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRoles.map((role, idx) => (
                          <tr key={role.id}>
                            <td>{idx + 1}</td>
                            <td>
                              <span className="fw-bold text-dark">{role.name}</span>
                            </td>
                            <td>{role.description || <span className="text-muted fs-12">No description</span>}</td>
                            <td>
                              <span className="badge bg-info-subtle text-info fs-12 px-2 py-1">
                                <i className="ri-user-line me-1"></i>
                                {role.employee_count} Employee(s)
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-primary-subtle text-primary fs-12 px-2 py-1">
                                <i className="ri-key-2-line me-1"></i>
                                {role.permission_count} Permission(s)
                              </span>
                            </td>
                            <td>
                              {role.status === 'active' ? (
                                <span className="badge bg-success-subtle text-success">Active</span>
                              ) : (
                                <span className="badge bg-danger-subtle text-danger">Inactive</span>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex gap-2 justify-content-end">
                                {hasPermission('master_roles.edit') && (
                                  <Link
                                    to={`/superadmin/master-roles/${role.id}/permissions`}
                                    className="btn btn-sm btn-soft-primary"
                                    title="Manage Role Permissions Matrix"
                                  >
                                    <i className="ri-shield-keyhole-line me-1"></i> Permissions
                                  </Link>
                                )}
                                {hasPermission('master_roles.edit') && (
                                  <button
                                    className="btn btn-sm btn-soft-secondary"
                                    onClick={() => handleOpenEditModal(role)}
                                    title="Edit Role Details"
                                  >
                                    <i className="ri-pencil-line"></i>
                                  </button>
                                )}
                                {hasPermission('master_roles.delete') && (
                                  <button
                                    className="btn btn-sm btn-soft-danger"
                                    onClick={() => setDeleteModal({ show: true, role })}
                                    title="Delete Role"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal: Create/Edit Role */}
        {showModal && (
          <div className="modal fade show d-block tab-modal-backdrop" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{isEditing ? 'Edit Master Role' : 'Add New Master Role'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Role Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Accounts, Sales, Operations"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Brief summary of duties and permissions"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Saving...' : (isEditing ? 'Update Role' : 'Create Role')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal.show && (
          <div className="modal fade show d-block tab-modal-backdrop" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title text-white">Confirm Delete Role</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setDeleteModal({ show: false, role: null })}></button>
                </div>
                <div className="modal-body text-center py-4">
                  <i className="ri-error-warning-line text-danger display-4 mb-2"></i>
                  <h5>Are you sure you want to delete role '{deleteModal.role?.name}'?</h5>
                  {deleteModal.role?.employee_count > 0 ? (
                    <p className="text-danger fw-semibold mt-2">
                      Warning: {deleteModal.role.employee_count} employee(s) are currently assigned to this role. You cannot delete it until employees are reassigned.
                    </p>
                  ) : (
                    <p className="text-muted fs-14">This action will remove all role permissions assigned to this role. This action cannot be undone.</p>
                  )}
                </div>
                <div className="modal-footer justify-content-center">
                  <button type="button" className="btn btn-light" onClick={() => setDeleteModal({ show: false, role: null })}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={deleteModal.role?.employee_count > 0}
                  >
                    Yes, Delete Role
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MasterRoleListing;
